import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, throwError, BehaviorSubject } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { Propietario } from "../model/propietario";
import Swal from "sweetalert2";
import { environment } from "../../../environments/environment";

export interface ResponsableItem {   // 👈 agregar "export"
  id: number;
  nombre: string;
}

@Injectable({
  providedIn: "root",
})

export class PropietarioService {
  private apiUrl = `${environment.apiUrl}/api/propietarios`;
  private apiConductorUrl = `${environment.apiUrl}/api/conductor/2`;

  private estadisticasSubject = new BehaviorSubject<any>(null);
  estadisticas$ = this.estadisticasSubject.asObservable();
  cedula: any;

  constructor(private http: HttpClient) {}

  private manejarError(error: HttpErrorResponse): Observable<never> {
    let mensaje = "Ocurrió un error inesperado";

    if (error.status === 400) {
      mensaje = "Solicitud incorrecta. Verifica los parámetros enviados.";
    } else if (error.status === 409) {
      mensaje = "El NIT ya está registrado.";
    } else if (error.error?.message) {
      mensaje = error.error.message;
    } else if (error.message) {
      mensaje = error.message;
    }

    Swal.fire({
      icon: "error",
      title: "Error",
      text: mensaje,
      confirmButtonText: "Aceptar",
    });

    return throwError(() => new Error(mensaje));
  }

  createPropietario(propietario: Propietario): Observable<Propietario> {
    return this.http
      .post<Propietario>(this.apiUrl, propietario)
      .pipe(catchError(this.manejarError));
  }

  getEPS(id: string): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/eps/${id}`)
      .pipe(catchError(this.manejarError));
  }

  getPropietarios(): Observable<{
    propietarios: Propietario[];
    conteoPorMes: Record<string, number>;
  }> {
    return this.http
      .get<{
        propietarios: Propietario[];
        conteoPorMes: Record<string, number>;
      }>(this.apiUrl)
      .pipe(catchError(this.manejarError));
  }

  updatePropietariosEstado(
    cedula: string,
    estadoPropietario: number,
  ): Observable<any> {
    const body = { cedula, estado: estadoPropietario };
    return this.http
      .put<any>(this.apiUrl, body)
      .pipe(catchError(this.manejarError));
  }

  getListadoLicencias(): Observable<any> {
    return this.http.get<any>(this.apiConductorUrl).pipe(
      tap(() => {}),
      catchError(this.manejarError),
    );
  }

  actualizarEstadisticas(estadisticas: any) {
    this.estadisticasSubject.next(estadisticas);
  }

  getPropietariobyIdPersona(idPersona: string): Observable<any> {
    const url = `${this.apiUrl}/${idPersona}`;
    return this.http.get<any>(url).pipe(
      catchError((error) => {
        console.error(
          `Error obteniendo propietarios para idPersona ${idPersona}:`,
          error,
        );
        let errorMsg = "Error obteniendo datos";
        if (error.status === 404) errorMsg = "Propietario no encontrado";
        else if (error.status === 401) errorMsg = "No autorizado";
        return throwError(() => new Error(errorMsg));
      }),
    );
  }

  updatePropietario(
    id: number,
    propietario: Propietario,
  ): Observable<Propietario> {
    return this.http.put<Propietario>(`${this.apiUrl}/${id}`, propietario).pipe(
      tap(() => {
        Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: "La Persona se actualizó correctamente.",
          confirmButtonText: "Aceptar",
        });
      }),
      catchError(this.manejarError),
    );
  }

  getPropietariosInactivos(): Observable<{
    propietarios: Propietario[];
    conteoPorMes: Record<string, number>;
  }> {
    return this.http
      .get<{
        propietarios: Propietario[];
        conteoPorMes: Record<string, number>;
      }>(`${this.apiUrl}/estado/0`)
      .pipe(catchError(this.manejarError));
  }

  validarCedula(cedula: number): Observable<{ existe: boolean }> {
    return this.http.get<{ existe: boolean }>(
      `${this.apiUrl}/validar-cedula/${cedula}`,
    );
  }

  getConductores(): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/conductor`)
      .pipe(catchError(this.manejarError));
  }

  getAdministrativos(): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/administrativos`)
      .pipe(catchError(this.manejarError));
  }

  getContarPropietarios(): Observable<{
    activos: number;
    inactivos: number;
    total: number;
  }> {
    return this.http
      .get<{
        activos: number;
        inactivos: number;
        total: number;
      }>(`${this.apiUrl}/conteo`)
      .pipe(catchError(this.manejarError));
  }


public getPropietariosEnLicencia(): Observable<any> {
  return this.http.get<any>(`${this.apiUrl}/conteo-licencia`)
    .pipe(
      catchError(this.manejarError),
    );
}


getProfesiogramaByCedula(cedula: string): Observable<any[]> {
      return this.http.get<any[]>(`${this.apiUrl}/examenes/${cedula}`, { withCredentials: true })
        .pipe(catchError((error) => throwError(() => error)));
    }


    getCedulaPesv(cedula: string): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/pesv/${encodeURIComponent(cedula)}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error); // <- esto evita que Angular lance por consola automáticamente
        }),
      );
  }

  getPropietarioCapacitacion(): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/capacitacion`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error); // <- esto evita que Angular lance por consola automáticamente
        }),
      );
  }
    getPlanAccion(): Observable<any> {
  return this.http.get<any>(`${this.apiUrl}/plan-accion`)
    .pipe(
      catchError(this.manejarError),
    );
}
}
